import { useCallback, useState } from 'react'

import { useResizeObserver } from '@core/hooks/useResizeObserver'

export type UseMeasureRect = Partial<Pick<DOMRectReadOnly, 'height' | 'width'>>
export type UseMeasureRef<E extends HTMLElement = HTMLElement> = (
  element: E,
) => void

/**
 * Custom hook that measures the size of a specified element using ResizeObserver API.
 * @template T - The type of element being measured.
 * @returns An object with a reference to the element and its dimensions.
 */
export function useMeasure<T extends HTMLElement>() {
  const [rect, setRect] = useState<UseMeasureRect>({ height: 0, width: 0 })

  const handleUpdateRectCB = useCallback(
    (entries) => {
      if (entries[0]) {
        const { width, height } = entries[0].contentRect
        setRect({ height, width })
      }
    },
    [setRect],
  )

  const ref = useResizeObserver<T>(handleUpdateRectCB)

  return { ref, rect }
}
